import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Chart from "chart.js/auto";
import Navbar from "../components/Navbar";

// --- helpers RUT ---
const cleanRut = (input = "") =>
  String(input).toUpperCase().replace(/[^0-9K]/g, "").trim();

// --- Formateo RUT: xx.xxx.xxx-x ---
const formatRutPretty = (input = "") => {
  const v = cleanRut(input);
  if (!v) return "";
  if (v.length < 2) return v;

  const dv = v.slice(-1);
  const cuerpo = v.slice(0, -1);

  let out = "";
  let c = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    out = cuerpo[i] + out;
    c++;
    if (c % 3 === 0 && i !== 0) out = "." + out;
  }
  return `${out}-${dv}`;
};

const useDebounced = (value, ms = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);

  return v;
};

const normalizarSede = (sede = "") => {
  const v = String(sede || "").trim();
  if (!v) return "Sin sede";
  return v;
};

const normalizarAlmuerzo = (almuerzo = "") => {
  const v = String(almuerzo || "").trim();
  if (!v) return "Sin almuerzo";
  return v;
};

const AlmuerzosDelDia = () => {
  const [rutInput, setRutInput] = useState("");
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));

  // Guardamos TODO lo del día acá (sin filtrar)
  const [itemsAll, setItemsAll] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [cargando, setCargando] = useState(false);

  const rutDebounced = useDebounced(rutInput, 300);
  const rutFiltroClean = useMemo(() => cleanRut(rutDebounced), [rutDebounced]);

  const chartSedeRef = useRef(null);
  const chartAlmuerzoRef = useRef(null);
  const chartSedeAlmuerzoRef = useRef(null);

  const chartSedeInstance = useRef(null);
  const chartAlmuerzoInstance = useRef(null);
  const chartSedeAlmuerzoInstance = useRef(null);

  const fetchByFecha = async (f) => {
    setCargando(true);
    setMensaje("");
    try {
      const resp = await axios.get("https://garibcasinos.com/api/get_programacion_dia.php", {
        params: { fecha: f },
      });

      if (resp.data?.ok) {
        const arr = Array.isArray(resp.data.data) ? resp.data.data : [];
        setItemsAll(arr);
        if (!arr.length) setMensaje("No hay almuerzos programados para ese día.");
      } else {
        setItemsAll([]);
        setMensaje(resp.data?.error || "No se pudo consultar.");
      }
    } catch (e) {
      setItemsAll([]);
      setMensaje(e?.response?.data?.error || e.message || "Error de red.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchByFecha(fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  const items = useMemo(() => {
    if (!rutFiltroClean) return itemsAll;

    return itemsAll.filter((r) => {
      const rutRow = cleanRut(r?.rut || r?.Rut || "");
      return rutRow.includes(rutFiltroClean);
    });
  }, [itemsAll, rutFiltroClean]);

  const totalRegistros = items.length;

  const resumenPorSede = useMemo(() => {
    const acc = {};

    items.forEach((r) => {
      const sede = normalizarSede(r?.sede);
      acc[sede] = (acc[sede] || 0) + 1;
    });

    return Object.entries(acc)
      .map(([sede, total]) => ({ sede, total }))
      .sort((a, b) => b.total - a.total);
  }, [items]);

  const resumenPorAlmuerzo = useMemo(() => {
    const acc = {};

    items.forEach((r) => {
      const almuerzo = normalizarAlmuerzo(r?.almuerzo);
      acc[almuerzo] = (acc[almuerzo] || 0) + 1;
    });

    return Object.entries(acc)
      .map(([almuerzo, total]) => ({ almuerzo, total }))
      .sort((a, b) => b.total - a.total);
  }, [items]);

  const resumenSedeAlmuerzo = useMemo(() => {
    const sedesSet = new Set();
    const almuerzosSet = new Set();
    const matriz = {};

    items.forEach((r) => {
      const sede = normalizarSede(r?.sede);
      const almuerzo = normalizarAlmuerzo(r?.almuerzo);

      sedesSet.add(sede);
      almuerzosSet.add(almuerzo);

      if (!matriz[sede]) matriz[sede] = {};
      matriz[sede][almuerzo] = (matriz[sede][almuerzo] || 0) + 1;
    });

    const sedes = Array.from(sedesSet).sort();
    const almuerzos = Array.from(almuerzosSet).sort();

    return { sedes, almuerzos, matriz };
  }, [items]);

  useEffect(() => {
    if (!chartSedeRef.current) return;

    if (chartSedeInstance.current) {
      chartSedeInstance.current.destroy();
      chartSedeInstance.current = null;
    }

    if (!resumenPorSede.length) return;

    const ctx = chartSedeRef.current.getContext("2d");

    chartSedeInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: resumenPorSede.map((x) => x.sede),
        datasets: [
          {
            label: "Almuerzos por sede",
            data: resumenPorSede.map((x) => x.total),
            backgroundColor: [
              "rgba(54, 162, 235, 0.7)",
              "rgba(75, 192, 192, 0.7)",
              "rgba(255, 159, 64, 0.7)",
              "rgba(153, 102, 255, 0.7)",
              "rgba(255, 99, 132, 0.7)",
              "rgba(201, 203, 207, 0.7)",
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: true, text: "Almuerzos por sede" },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
          },
        },
      },
    });
  }, [resumenPorSede]);

  useEffect(() => {
    if (!chartAlmuerzoRef.current) return;

    if (chartAlmuerzoInstance.current) {
      chartAlmuerzoInstance.current.destroy();
      chartAlmuerzoInstance.current = null;
    }

    if (!resumenPorAlmuerzo.length) return;

    const ctx = chartAlmuerzoRef.current.getContext("2d");

    chartAlmuerzoInstance.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels: resumenPorAlmuerzo.map((x) => x.almuerzo),
        datasets: [
          {
            label: "Almuerzos por tipo",
            data: resumenPorAlmuerzo.map((x) => x.total),
            backgroundColor: [
              "rgba(54, 162, 235, 0.75)",
              "rgba(255, 99, 132, 0.75)",
              "rgba(255, 205, 86, 0.75)",
              "rgba(75, 192, 192, 0.75)",
              "rgba(153, 102, 255, 0.75)",
              "rgba(255, 159, 64, 0.75)",
              "rgba(201, 203, 207, 0.75)",
              "rgba(100, 181, 246, 0.75)",
              "rgba(129, 199, 132, 0.75)",
              "rgba(240, 98, 146, 0.75)",
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
          title: { display: true, text: "Distribución por almuerzo" },
        },
      },
    });
  }, [resumenPorAlmuerzo]);

  useEffect(() => {
    if (!chartSedeAlmuerzoRef.current) return;

    if (chartSedeAlmuerzoInstance.current) {
      chartSedeAlmuerzoInstance.current.destroy();
      chartSedeAlmuerzoInstance.current = null;
    }

    const { sedes, almuerzos, matriz } = resumenSedeAlmuerzo;
    if (!sedes.length || !almuerzos.length) return;

    const colores = [
      "rgba(54, 162, 235, 0.75)",
      "rgba(255, 99, 132, 0.75)",
      "rgba(255, 205, 86, 0.75)",
      "rgba(75, 192, 192, 0.75)",
      "rgba(153, 102, 255, 0.75)",
      "rgba(255, 159, 64, 0.75)",
      "rgba(201, 203, 207, 0.75)",
      "rgba(100, 181, 246, 0.75)",
      "rgba(129, 199, 132, 0.75)",
      "rgba(240, 98, 146, 0.75)",
    ];

    const datasets = almuerzos.map((almuerzo, i) => ({
      label: almuerzo,
      data: sedes.map((sede) => matriz[sede]?.[almuerzo] || 0),
      backgroundColor: colores[i % colores.length],
      borderWidth: 1,
    }));

    const ctx = chartSedeAlmuerzoRef.current.getContext("2d");

    chartSedeAlmuerzoInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: sedes,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          title: { display: true, text: "Almuerzos por sede y por tipo" },
        },
        scales: {
          x: {
            stacked: false,
          },
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
          },
        },
      },
    });
  }, [resumenSedeAlmuerzo]);

  useEffect(() => {
    return () => {
      if (chartSedeInstance.current) chartSedeInstance.current.destroy();
      if (chartAlmuerzoInstance.current) chartAlmuerzoInstance.current.destroy();
      if (chartSedeAlmuerzoInstance.current) chartSedeAlmuerzoInstance.current.destroy();
    };
  }, []);

  const consultar = () => fetchByFecha(fecha);

  return (
    <>

      <section className="section">
        <div className="container">
          <h1 className="title">Almuerzos del día</h1>

          <div className="box">
            <div className="columns">
              <div className="column is-5">
                <div className="field">
                  <label className="label">Buscar por RUT</label>
                  <div className="control">
                    <input
                      className="input"
                      type="text"
                      placeholder="12.345.678-9"
                      value={rutInput}
                      onChange={(e) => setRutInput(formatRutPretty(e.target.value))}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className="column is-4">
                <div className="field">
                  <label className="label">Fecha</label>
                  <div className="control">
                    <input
                      className="input"
                      type="date"
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div
                className="column is-3"
                style={{ display: "flex", alignItems: "flex-end" }}
              >
                <button
                  className={`button is-primary is-fullwidth ${cargando ? "is-loading" : ""}`}
                  type="button"
                  onClick={consultar}
                >
                  Recargar
                </button>
              </div>
            </div>

            {mensaje && <div className="notification is-info">{mensaje}</div>}

            <div className="is-flex is-justify-content-space-between is-align-items-center mb-4">
              <strong>Fecha: {fecha}</strong>
              <span>
                Total: {totalRegistros}
                {rutFiltroClean ? (
                  <span className="tag is-light ml-2" style={{ marginLeft: 10 }}>
                    Filtro RUT
                  </span>
                ) : null}
              </span>
            </div>

            {/* CONTADORES */}
            <div className="columns is-multiline mb-4">
              <div className="column is-3">
                <div className="box has-background-primary-light">
                  <p className="heading">Total del día</p>
                  <p className="title is-4">{totalRegistros}</p>
                </div>
              </div>

              {resumenPorSede.map((item) => (
                <div className="column is-3" key={item.sede}>
                  <div className="box has-background-light">
                    <p className="heading">{item.sede}</p>
                    <p className="title is-4">{item.total}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* GRÁFICOS */}
            <div className="columns is-multiline">
              <div className="column is-6">
                <div className="box">
                  <div style={{ height: 320 }}>
                    <canvas ref={chartSedeRef}></canvas>
                  </div>
                </div>
              </div>

              <div className="column is-6">
                <div className="box">
                  <div style={{ height: 320 }}>
                    <canvas ref={chartAlmuerzoRef}></canvas>
                  </div>
                </div>
              </div>

              <div className="column is-12">
                <div className="box">
                  <div style={{ height: 380 }}>
                    <canvas ref={chartSedeAlmuerzoRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

            {/* TABLA RESUMEN POR SEDE */}
            <div className="box mt-5">
              <h2 className="title is-5">Resumen por sede</h2>
              <div style={{ overflowX: "auto" }}>
                <table className="table is-fullwidth is-striped is-hoverable">
                  <thead>
                    <tr>
                      <th>Sede</th>
                      <th>Total almuerzos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumenPorSede.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="has-text-centered has-text-grey">
                          Sin datos para mostrar.
                        </td>
                      </tr>
                    ) : (
                      resumenPorSede.map((r) => (
                        <tr key={r.sede}>
                          <td>{r.sede}</td>
                          <td>{r.total}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* TABLA DETALLE */}
            <div className="box mt-5">
              <h2 className="title is-5">Detalle</h2>
              <div style={{ overflowX: "auto" }}>
                <table className="table is-striped is-hoverable is-fullwidth">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>RUT</th>
                      <th>Nombre</th>
                      <th>Almuerzo</th>
                      <th>Sede</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="has-text-centered has-text-grey">
                          {rutFiltroClean
                            ? "Sin resultados para ese RUT en esta fecha."
                            : "No hay registros para esta fecha."}
                        </td>
                      </tr>
                    ) : (
                      items.map((r, i) => (
                        <tr key={`${cleanRut(r.rut || r.Rut || "")}-${r.fecha || fecha}-${i}`}>
                          <td>{i + 1}</td>
                          <td>{r.rut || r.Rut || "-"}</td>
                          <td>{r.nombre || r.Nombre || "-"}</td>
                          <td>{r.almuerzo || "-"}</td>
                          <td>{r.sede || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AlmuerzosDelDia;